<template>
  <div id="passport-stat-view">
    <AppHeader>Reckon Passport</AppHeader>

    <main class="stat-page">
      <v-container v-if="passport && stat">
        <v-btn outlined class="back-link mb-6" :to="passportRoute">
          <v-icon left>mdi-arrow-left</v-icon>
          {{ passport.preferredName }}'s Passport
        </v-btn>

        <header class="stat-hero">
          <div class="stat-icon"><v-icon>{{ stat.icon }}</v-icon></div>
          <div>
            <p class="kicker">Passport Stat</p>
            <h1>{{ stat.label }}</h1>
            <p>{{ stat.description }}</p>
          </div>
          <strong class="stat-count">{{ stat.count }}</strong>
        </header>

        <section class="source-section">
          <div class="section-heading">
            <p class="kicker">What Counts</p>
            <h2>Records included for {{ passport.preferredName }}</h2>
          </div>

          <div class="source-grid">
            <article
              v-for="item in stat.items"
              :key="item.id"
              class="source-card"
              :class="{ 'source-card--linked': recordRoute(item) || (item.sourceRoutes && item.sourceRoutes.length === 1) }"
            >
              <router-link
                v-if="recordRoute(item)"
                class="source-card-link"
                :to="recordRoute(item)"
                :aria-label="`View ${item.title} details`"
              />
              <router-link
                v-else-if="item.sourceRoutes && item.sourceRoutes.length === 1"
                class="source-card-link"
                :to="item.sourceRoutes[0].route"
                :aria-label="`View ${item.sourceRoutes[0].label}`"
              />
              <div class="source-title-row">
                <h3>{{ item.title }}</h3>
                <span class="source-tag">{{ item.eyebrow }}</span>
              </div>
              <p v-if="item.needsLocation" class="source-status">Venue not yet documented</p>
              <p class="source-description">{{ item.description }}</p>
              <ul v-if="item.details && item.details.length">
                <li v-for="detail in item.details" :key="detail">{{ detail }}</li>
              </ul>
              <nav
                v-if="item.sourceRoutes && item.sourceRoutes.length > 1"
                class="source-links"
                aria-label="Contributing events"
              >
                <router-link
                  v-for="source in item.sourceRoutes"
                  :key="`${item.id}-${source.label}`"
                  :to="source.route"
                >
                  {{ source.label }}
                  <v-icon small>mdi-arrow-right</v-icon>
                </router-link>
              </nav>
            </article>
          </div>
        </section>
      </v-container>

      <v-container v-else>
        <section class="missing-state">
          <h1>Passport stat not found</h1>
          <p>This statistic is not available for the requested passport.</p>
          <v-btn outlined class="back-link" :to="passportRoute">Back to Passport</v-btn>
        </section>
      </v-container>
    </main>

    <AppFooter></AppFooter>
  </div>
</template>

<script>
import { passportBySlug } from '../data/passports';
import { supportsPassportRecordDetail } from '../lib/passports/recordDetails';
import AppFooter from '../components/Footer.vue';
import AppHeader from '../components/Header.vue';

export default {
  name: 'PassportStatView',
  components: { AppFooter, AppHeader },
  computed: {
    passport() {
      return passportBySlug[this.$route.params.employeeSlug] || null;
    },
    stat() {
      return this.passport?.statDetails[this.$route.params.statKey] || null;
    },
    passportRoute() {
      return {
        name: 'passport',
        params: { employeeSlug: this.$route.params.employeeSlug },
      };
    },
  },
  methods: {
    recordRoute(item) {
      if (this.$route.params.statKey === 'badges' && item.activityRoute) return item.activityRoute;
      if (!supportsPassportRecordDetail(this.$route.params.statKey)) return null;
      return {
        name: 'passport-record',
        params: {
          employeeSlug: this.$route.params.employeeSlug,
          statKey: this.$route.params.statKey,
          recordId: item.id,
        },
      };
    },
  },
};
</script>

<style scoped>
.stat-page {
  background: #050505;
  color: #f5f5f5;
  min-height: 75vh;
  padding: 48px 0 72px;
}

.back-link {
  border-color: rgba(255, 255, 255, 0.3) !important;
  color: #fff !important;
}

.stat-hero {
  align-items: center;
  background: linear-gradient(135deg, rgba(226, 50, 50, 0.18), rgba(255, 255, 255, 0.04));
  border: 1px solid rgba(226, 50, 50, 0.35);
  border-radius: 12px;
  display: grid;
  gap: 22px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  padding: 28px;
}

.stat-icon {
  align-items: center;
  background: #e23232;
  border-radius: 50%;
  display: flex;
  height: 64px;
  justify-content: center;
  width: 64px;
}

.stat-icon .v-icon {
  color: #fff;
  font-size: 34px;
}

.kicker {
  color: #e85a5a;
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  margin: 0 0 6px;
  text-transform: uppercase;
}

.stat-hero h1,
.section-heading h2,
.source-card h3,
.missing-state h1 {
  color: #fff;
  letter-spacing: 0;
}

.stat-hero h1 {
  font-size: clamp(2rem, 5vw, 3.4rem);
  margin: 0;
}

.stat-hero p:last-child {
  color: #d6d6d6;
  margin: 8px 0 0;
  max-width: 720px;
}

.stat-count {
  color: #fff;
  font-size: clamp(3rem, 8vw, 5.5rem);
  line-height: 1;
}

.source-section {
  margin-top: 44px;
}

.section-heading {
  margin-bottom: 18px;
}

.section-heading h2 {
  font-size: 1.8rem;
  margin: 0;
}

.source-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.source-card {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  padding: 20px;
  position: relative;
  transition: border-color 160ms ease, transform 160ms ease;
}

.source-card--linked:hover,
.source-card--linked:focus-within {
  border-color: rgba(232, 90, 90, 0.8);
  transform: translateY(-2px);
}

.source-card-link {
  inset: 0;
  position: absolute;
  z-index: 1;
}

.source-card h3 {
  font-size: 1.2rem;
  margin: 0;
}

.source-title-row {
  align-items: flex-start;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  margin-bottom: 10px;
}

.source-tag {
  background: rgba(232, 90, 90, 0.12);
  border: 1px solid rgba(232, 90, 90, 0.45);
  border-radius: 999px;
  color: #ef8888;
  flex: 0 0 auto;
  font-size: 0.62rem;
  font-weight: 900;
  letter-spacing: 0.06em;
  padding: 3px 7px;
  text-transform: uppercase;
}

.source-description,
.source-card li,
.missing-state p {
  color: #d0d0d0;
}

.source-description {
  margin: 0;
}

.source-status {
  color: #f2b84b;
  font-size: 0.82rem;
  font-weight: 800;
  margin: -2px 0 8px;
  text-transform: uppercase;
}

.source-card ul {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  list-style: none;
  margin: 14px 0 0;
  padding: 12px 0 0;
}

.source-card li + li {
  margin-top: 5px;
}

.source-links {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: grid;
  gap: 8px;
  margin-top: 14px;
  padding-top: 12px;
  position: relative;
  z-index: 2;
}

.source-links a {
  align-items: center;
  color: #ef7777;
  display: flex;
  font-weight: 800;
  justify-content: space-between;
  text-decoration: none;
}

.source-links .v-icon {
  color: currentColor;
}

.missing-state {
  background: #101010;
  border-radius: 10px;
  padding: 48px;
  text-align: center;
}

@media (max-width: 900px) {
  .source-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .stat-page {
    padding-top: 28px;
  }

  .stat-hero {
    grid-template-columns: auto minmax(0, 1fr);
    padding: 22px;
  }

  .stat-count {
    grid-column: 1 / -1;
  }

  .source-grid {
    grid-template-columns: 1fr;
  }
}
</style>
